import { useCallback, useMemo, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { PWSSessionBackend, COAST_MESSAGES } from "@inferno/renderer-shared-core";
import { ILog } from "@ihr-radioedit/inferno-core";
import { Button, ButtonKind } from "../../../../../core/ui";
import Spinner from "../../../../../core/ui/Spinner.component";
import { axiosErrorReason } from "../../../lib/coastCommon";
import { FORM_ERRORS, SimpleInput, validatePasswordHintContains, validatePasswordsMatch } from "../../forms";
import { CoastAlertDisplayError } from "../common/coast-alert/CoastAlertDisplay.component";

const log = ILog.logger("CoastSignUpSubmitPassword.component");

interface PasswordFormInputs {
  confirmPassword: string;
  passwordHint: string;
  password: string;
}

interface CoastSignUpSubmitPasswordProps {
  session: PWSSessionBackend;
  onCancel: () => void;
  emailToken: string;
}

export const CoastSignUpSubmitPassword = ({ onCancel, emailToken, session }: CoastSignUpSubmitPasswordProps) => {
  const { errors, register, handleSubmit, watch } = useForm<PasswordFormInputs>();

  const [isProcessing, setIsProcessing] = useState(false);
  const [formError, setFormError] = useState<string>();

  const userEmail = useMemo(() => {
    if (typeof window === "undefined") {
      return null;
    }

    try {
      return JSON.parse(atob(emailToken.split(".")[1])).email;
    } catch {
      // do nothing
    }
  }, [emailToken]);

  const onSubmit: SubmitHandler<PasswordFormInputs> = useCallback(
    async ({ password, passwordHint }, event) => {
      event?.preventDefault();

      setIsProcessing(true);
      try {
        const resp = await session.memberService.createUser(emailToken, {
          password,
          custom: { password_hint: passwordHint },
        });
        if (resp.loginData) {
          log.info("Setting session data");
          session.sessionUpdated({
            expiresAt: (resp.loginData.created_at + resp.loginData.expires_in) * 1000,
            createdAt: resp.loginData.created_at * 1000,
            refreshToken: resp.loginData.refresh_token,
            accessToken: resp.loginData.access_token,
            authenticated: true,
            profile: resp.user,
          });
        } else {
          log.warn("Got no session data in Create User response");
        }

        window.location.assign("?step=subscription");
      } catch (e) {
        setFormError(axiosErrorReason(e) || COAST_MESSAGES.UNEXPECTED_ERROR);
      }
      setIsProcessing(false);
    },
    [emailToken, session],
  );

  return (
    <div className="component-coast-verify-email">
      <h3 className="step-indicator">Step 3 of 7</h3>
      <h2 className="section-title">Create Your Password</h2>

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* do this so browsers recognize this as a signup form. So you get stuff like password suggestion */}
        {userEmail ? <input type="email" hidden={true} value={userEmail} name="email" autoComplete="username" /> : null}

        <SimpleInput
          placeholder="Enter your password"
          rules={{
            maxLength: { value: 32, message: "Password can't be longer than 32 characters" },
            minLength: { value: 6, message: "Password can't be shorter that 6 characters" },
            required: FORM_ERRORS.required,
          }}
          autoComplete="new-password"
          error={errors.password}
          register={register}
          label="Password"
          name="password"
          type="password"
        />

        <SimpleInput
          placeholder="Confirm your password"
          rules={{
            validate: v => validatePasswordsMatch(watch("password"), v),
            required: FORM_ERRORS.required,
          }}
          error={errors.confirmPassword}
          autoComplete="new-password"
          register={register}
          label="Confirm Password"
          name="confirmPassword"
          type="password"
        />

        <SimpleInput
          placeholder="Enter your password hint"
          rules={{ validate: v => validatePasswordHintContains(v, watch("password")) }}
          error={errors.passwordHint}
          register={register}
          label="Password Hint"
          name="passwordHint"
          optional={true}
          type="text"
        />

        <div className="button-prev-next">
          <Button
            kind={ButtonKind.CUSTOMIZED}
            className="button-continue"
            type="submit"
            disabled={!!errors.password || !!errors.confirmPassword || isProcessing}
          >
            {isProcessing ? <Spinner visible={true} /> : "Submit"}
          </Button>
          <Button className="button-previous" type="button" disabled={true} kind={ButtonKind.CUSTOMIZED}>
            Previous
          </Button>
        </div>
      </form>

      <CoastAlertDisplayError isDisplayed={!!formError} message={formError} />

      <Button className="button-cancel" click={onCancel} kind={ButtonKind.CUSTOMIZED}>
        Cancel
      </Button>
    </div>
  );
};
